import { Injectable } from '@angular/core';
import type { MapboxMap } from '@polaris-customerweb/sharedcomponents.core';
import { ConsoleLogService } from '@polaris-customerweb/sharedcomponents.shared';
import { startsWith } from 'lodash-es';
import { FilterSpecification, PointLike } from 'mapbox-gl';
import { MapService } from 'src/app/services/map.service';
import { GeoJsonFeatureCollection } from '../models/geojson-feature-collection';

@Injectable({ providedIn: 'root' })
export class MapContentMapService {
    layerIds: string[] = [];
    minZoom = 10;

    protected _layerIdsSelectable: string[];
    protected layerPrefixActive = 'active';
    protected layerPrefixEditing = 'editing';
    protected layerPrefixSelected = 'selected';
    protected layerPrefixUnselected = 'unselected';
    protected sourceId = 'sourceId';
    protected editSourceId = 'editSourceId';
    protected initialSelectedIds:
        | string
        | number
        | string[]
        | number[]
        | undefined;
    protected layerIdsActive: string[];
    protected layerIdsEditing: string[];
    protected layerIdsSelected: string[];
    protected layerIdsUnselected: string[];
    protected allLayerIds: readonly string[];
    protected ignoreLayerIds: string[] = [];

    constructor(
        protected consoleLogService: ConsoleLogService,
        protected mapService: MapService
    ) {}

    get layerIdsSelectable(): string[] {
        return this._layerIdsSelectable || [];
    }

    addSources(mapboxMap: MapboxMap): void {
        const layerIds = this.mapService
            .getLayerIds(mapboxMap)
            .filter((layerId) => !this.ignoreLayerIds.includes(layerId));
        this.allLayerIds = layerIds;
        this.getLayerIds(layerIds);
        this.createLayerSources(mapboxMap);
        this.setLayerSources(mapboxMap);

        this.setSelectedFilter(mapboxMap, this.initialSelectedIds);

        const minZoom = this.getMinZoomFromStyle(mapboxMap);
        if (minZoom !== undefined) {
            this.minZoom = minZoom;
        }

        // addSources may get called multiple times in the lifetime of a map.
        // unset initial selected ID for future calls.
        this.initialSelectedIds = undefined;
    }

    getIdAtPoint(mapboxMap: MapboxMap, point: PointLike): string {
        const features = this.mapService.getFeaturesAtPoint(
            mapboxMap,
            point,
            this.layerIds
        );
        let featureId = '';

        for (let i = 0, len = features.length; i < len; i++) {
            const id = features[i].properties?.id;
            if (id) {
                featureId = id;
                break;
            }
        }

        return featureId;
    }

    setEditLayersVisibility(mapboxMap: MapboxMap, visible: boolean) {
        if (!this.layerIdsEditing) {
            return;
        }
        if (visible) {
            this.layerIdsEditing.forEach((layerId) => {
                this.mapService.showLayer(mapboxMap, layerId);
            });
        } else {
            this.layerIdsEditing.forEach((layerId) => {
                this.mapService.hideLayer(mapboxMap, layerId);
            });
        }
    }

    setEditSourceData(
        mapboxMap: MapboxMap,
        geojson?: GeoJSON.Feature | GeoJSON.FeatureCollection
    ): void {
        if (!geojson) {
            this.mapService.setSourceData(
                mapboxMap,
                this.editSourceId,
                new GeoJsonFeatureCollection()
            );
            this.consoleLogService.debug(`clear ${this.editSourceId} map data`);
            return;
        }

        this.mapService.setSourceData(mapboxMap, this.editSourceId, geojson);
        this.consoleLogService.debug(`set ${this.editSourceId} map data`);
    }

    setLayerVisibility(mapboxMap: MapboxMap, visible: boolean): void {
        if (!this.layerIdsUnselected) {
            return;
        }
        if (visible) {
            this.layerIdsUnselected.forEach((layerId) => {
                this.mapService.showLayer(mapboxMap, layerId);
            });
        } else {
            this.layerIdsUnselected.forEach((layerId) => {
                this.mapService.hideLayer(mapboxMap, layerId);
            });
        }
    }

    // Dim existing planned ride to 60% in ride planning mode.
    setUnselectedLayerOpacity(mapboxMap: MapboxMap, visible: boolean): void {
        // Remove layers that cause getPaintProperty value of undefined
        const layersWithLineOpacityProp = this.layerIdsUnselected.filter(
            (p) =>
                p !== 'plannedRidesUnselectedDot' &&
                p !== 'plannedRidesUnselected'
        );
        if (visible) {
            layersWithLineOpacityProp.forEach((layerId) => {
                this.mapService.setLayerOpacity(mapboxMap, layerId, 0.6);
            });
        } else {
            layersWithLineOpacityProp.forEach((layerId) => {
                this.mapService.setLayerOpacity(mapboxMap, layerId, 1);
            });
        }
    }

    setMultiSelectFilter(
        mapboxMap: MapboxMap,
        selectedIds?: string | string[],
        skipUnselected = false
    ): void {
        const layers = skipUnselected ? this.layerIdsSelected : this.layerIds;
        if (!selectedIds || selectedIds.length === 0) {
            selectedIds = ['SELECTED_ID'];
        }
        if (!Array.isArray(selectedIds)) {
            selectedIds = [selectedIds];
        }

        layers.forEach((layerId) => {
            let filter = mapboxMap.getFilter(layerId) as FilterSpecification;
            filter = this.updateFilterWithSelectedIds(filter, selectedIds);

            this.mapService.setFilter(mapboxMap, layerId, filter);
        });
    }

    setSelectedFilter(
        mapboxMap: MapboxMap,
        selectedIds?: string | number | string[] | number[],
        skipUnselected = false
    ): void {
        const layers = skipUnselected ? this.layerIdsSelected : this.layerIds;
        if (!layers || layers.length === 0) {
            // sources have not been set, store the selected ID and exit, we'll set filter on add sources
            this.initialSelectedIds = selectedIds;
            return;
        }
        this.mapService.applyLayerFilters(mapboxMap, layers, selectedIds);
    }

    setSourceData(
        mapboxMap: MapboxMap,
        geojson?: GeoJSON.Feature | GeoJSON.FeatureCollection
    ): void {
        if (!geojson) {
            this.mapService.setSourceData(
                mapboxMap,
                this.sourceId,
                new GeoJsonFeatureCollection()
            );
            this.consoleLogService.debug(`clear ${this.sourceId} map data`);
            return;
        }

        this.mapService.setSourceData(mapboxMap, this.sourceId, geojson);
        this.consoleLogService.debug(`set ${this.sourceId} map data`);
    }

    protected setSourceId(sourceId: string): void {
        this.sourceId = sourceId;
        this.editSourceId = `${sourceId}Edit`;
        this.layerPrefixActive = `${sourceId}active`;
        this.layerPrefixEditing = `${sourceId}edit`;
        this.layerPrefixSelected = `${sourceId}selected`;
        this.layerPrefixUnselected = `${sourceId}unselected`;
    }

    protected createLayerSources(mapboxMap: MapboxMap): void {
        this.mapService.addSource(
            mapboxMap,
            this.sourceId,
            this.mapService.createGeojsonSource()
        );

        if (this.layerIdsEditing?.length) {
            this.mapService.addSource(
                mapboxMap,
                this.editSourceId,
                this.mapService.createGeojsonSource()
            );
        }
    }

    protected getLayerIds(layerIds: Readonly<string[]>): void {
        this.layerIdsActive = layerIds.filter((id) => {
            return startsWith(
                id.toLowerCase(),
                this.layerPrefixActive.toLowerCase()
            );
        });
        this.layerIdsEditing = layerIds.filter((id) => {
            return startsWith(
                id.toLowerCase(),
                this.layerPrefixEditing.toLowerCase()
            );
        });
        this.layerIdsSelected = layerIds.filter((id) => {
            return startsWith(
                id.toLowerCase(),
                this.layerPrefixSelected.toLowerCase()
            );
        });
        this.layerIdsUnselected = layerIds.filter((id) => {
            return (
                startsWith(
                    id.toLowerCase(),
                    this.layerPrefixUnselected.toLowerCase()
                ) || id.toLowerCase() === this.sourceId.toLowerCase()
            );
        });
        this.layerIds = [
            ...this.layerIdsEditing,
            ...this.layerIdsUnselected,
            ...this.layerIdsSelected,
        ];
        this.consoleLogService.debug(
            `${this.sourceId} layer IDs`,
            this.layerIds
        );
    }

    protected getMinZoomFromStyle(mapboxMap: MapboxMap): number | undefined {
        let minZoom: number | undefined;

        for (let i = 0, len = this.layerIdsUnselected.length; i < len; i++) {
            const layerId = (this.layerIdsUnselected || [])[i];
            const style = this.mapService.getLayerStyle(mapboxMap, layerId);

            if (typeof style?.minzoom === 'number') {
                minZoom = style.minzoom;
                break;
            }
        }

        return minZoom;
    }

    /**
     * @param filter
     * @param selectedIds
     * @description update the filter expression to set the selected ids on the filter
     * assumes filter looks like this:
     * ['match', ['get', 'id'], ['SELECTED_ID], true, false]
     * OR
     * [
     *     'all',
     *     [
     *         ... other filters (optional),
     *         ['match', ['get', 'id'], ['SELECTED_ID'], true, false],
     *         ... other filters (optional)
     *     ]
     * ]
     * OR
     * [
     *     'all',
     *     [
     *         ... other filters (optional),
     *         'all': [
     *                ... other filters (optional),
     *                ['match', ['get', 'id'], ['SELECTED_ID'], true, false],
     *                ... other filters (optional),
     *         ]
     *         ... other filters (optional)
     *     ]
     * ]
     * looks for matching filter snippet and replaces id array with new ids
     * if it can't find a match it simply skips
     */
    protected updateFilterWithSelectedIds(
        filter: FilterSpecification,
        selectedIds?: string | string[]
    ) {
        if (filter[0] === 'match' && filter[1]?.[1] === 'id') {
            filter[2] = selectedIds;
        } else if (filter[0] === 'all') {
            for (const x of filter) {
                if (x[0] === 'match' && x[1]?.[1] === 'id') {
                    x[2] = selectedIds;
                } else if (x[0] === 'all') {
                    for (const y of x) {
                        if (y[0] === 'match' && y[1]?.[1] === 'id') {
                            y[2] = selectedIds;
                        }
                    }
                }
            }
        }
        return filter;
    }

    private setLayerSources(mapboxMap: MapboxMap): void {
        [
            ...this.layerIdsActive,
            ...this.layerIdsSelected,
            ...this.layerIdsUnselected,
        ].forEach((layerId) => {
            this.mapService.replaceLayer(mapboxMap, layerId, this.sourceId);
        });

        this.layerIdsEditing.forEach((layerId) => {
            this.mapService.replaceLayer(mapboxMap, layerId, this.editSourceId);
        });

        this._layerIdsSelectable = [
            ...this.layerIdsUnselected,
            ...this.layerIdsSelected,
        ];
    }
}
